<template>
  <div class="flex-1 flex flex-col max-h-full" v-allow:auth="'check.read'">
    <sub-header
      :handleSearchFilter="handleSearchFilter"
      :showBtn="checkPermission('check.create')"
      :handleClick="showCheckModal"
      :buttonText="$t('pages.check_admin.create_check')"
    />
    <div
      class="flex flex-1 flex-col flex-grow max-h-full max-w-full overflow-auto scroll-bar"
    >
      <Table
        id="checkTable"
        :columns="columnOption"
        :key="pageData.total"
        :rows="translatedRows"
        @handleClone="handleCloneCheck($event)"
        :isLoading="isLoading"
        :filters="serverParams"
        :filterOptions="columnFilterOptions"
        :filterOptionsAsync="columnFilterOptionsAsync"
        @per-page-change="onPageChange($event, true)"
        @page-change="onPageChange($event)"
        @column-filter="onColumnFilters($event)"
        :paginationOptions="paginationOptions"
        :totalRows="pageData.total"
        @filters-clear="clearFilters"
        sortingEnabled
        @sort-change="onSortChange"
        @search-filter="getSearchOptionsTick($event)"
      >
        <template #link="customData">
          <div class="truncate max-w-full whitespace-nowrap cursor-pointer">
            <span v-tippy :content="customData.customData.name">
              {{ customData.customData.name }}
            </span>
          </div>
        </template>
        <template #source="customData">
          <span
            class="inline-flex rounded-sm px-2 text-xs font-semibold leading-5 truncate"
            :class="
              customData.customData.is_custom
                ? 'bg-green-100 text-green-800'
                : 'bg-blue-200 text-blue-800'
            "
            >{{ isRestrictedOrCustom(customData.customData) }}</span
          >
        </template>
        <template #show_info="customData">
          <p
            :class="
              customData.customData && customData.customData.in_packages > 0
                ? 'text-primary hover:text-primary-300 hover:underline cursor-pointer'
                : 'text-gray-500'
            "
            @click.stop="setCheckPackages(customData.customData)"
          >
          {{ $t('pages.check_admin.used_in_packages', { value: customData.customData.in_packages }) }}
          </p>
        </template>
        <template #actions="customData">
          <div class=" flex justify-around">
            <button
              class="h-6 w-4 mx-2 mt-1 disabled:opacity-50"
              @click="handleCloneCheck(customData.customData)"
              v-tippy
              :content="'Clone'"
              :disabled="
                !checkPermission('check.edit') &&
                  !checkPermission('check.create')
              "
            >
              <font-awesome-icon
                icon="clone"
                class="text-blue-600 text-lg fa-2x"
                :class="
                  customData.customData
                    ? 'text-primary hover:text-dvbrandhoveron hover:underline cursor-pointer'
                    : 'text-gray-500'
                "
              />
            </button>
            <router-link
              :to="
                customData.customData.lock
                  ? {}
                  : {
                      name: 'check admin details',
                      params: { id: customData.customData.id, action: 'edit' },
                    }
              "
              class="text-primary truncate max-w-full whitespace-nowrap cursor-pointer hover:text-primary"
            >
              <button
                class="h-8 w-4 disabled:opacity-50 mx-2"
                :disabled="
                  customData.customData.lock == true ||
                    !checkPermission('check.edit')
                "
                v-tippy
                :content="'Edit'"
              >
                <font-awesome-icon
                  icon="edit"
                  class="text-blue-600 text-lg fa-3x"
                  :class="
                    customData.customData
                      ? 'text-primary hover:text-dvbrandhoveron hover:underline cursor-pointer'
                      : 'text-gray-500'
                  "
                />
              </button>
            </router-link>
            <router-link
              :to="{
                name: 'check admin details',
                params: { id: customData.customData.id, action: 'view' },
              }"
              class="text-primary truncate max-w-full whitespace-nowrap cursor-pointer hover:text-primary"
            >
              <button
                class="h-8 w-4 disabled:opacity-50 mx-2"
                v-tippy
                :content="'View'"
                :disabled="!checkPermission('check.read')"
              >
                <font-awesome-icon
                  icon="eye"
                  class="text-blue-600 text-lg fa-3x"
                  :class="
                    customData.customData
                      ? 'text-primary hover:text-dvbrandhoveron hover:underline cursor-pointer'
                      : 'text-gray-500'
                  "
                />
              </button>
            </router-link>
          </div>
        </template>
      </Table>
    </div>

    <!-- Create Check Modal -->
    <modal-content
      ref="check-modal"
      :max-width="480"
      name="check-modal"
      :title="$t('pages.email_admin.add_new_check')"
      @close="closeCheckModal"
      :showFooter="false"
      :contentScrollable="false"
      v-allow:auth="'check.create'"
    >
      <template #content>
        <FormulateForm #default="{ hasErrors }" class="min-h-full flex-1">
          <div class="add-check__modal-content h-full overflow-hidden">
            <div class="form flex w-full flex-col flex-1 overflow-auto scroll-bar pr-1">
              <div class="flex flex-col mb-4">
                <span class="text-gray-6 inline-block py-2 text-base-content"
                  >{{ $t('pages.check_admin.check_category') }}
                  <span class="text-red-500 ml-1 text-lg mr-auto"> * </span>
                </span>
                <Dropdown
                  reposition
                  searchable
                  :placeholder="$t('pages.check_admin.choose_check_category')"
                  :options="checkCategories"
                  :colors="{
                    text: '#282828',
                    bg: 'white',
                    svg: '#282828',
                    border: '#bcbcbc',
                  }"
                  :config="{ label: 'name', trackBy: 'id' }"
                  :value="checkData.checkCategory"
                  @input="(value) => updateCheckCategory(value)"
                  :limit="1"
                  minWidth="102px"
                  maxWidth="100%"
                  :loading="checkTypeFetching"
                />
              </div>
              <div class="flex flex-col mb-4">
                <span class="text-gray-6 inline-block py-2 text-base-content"
                  > {{ $t('pages.check_admin.check_type') }}
                  <span class="text-red-500 ml-1 text-lg mr-auto"> * </span>
                </span>
                <Dropdown
                  reposition
                  searchable
                  :placeholder="$t('pages.check_admin.add_this_as_new_check_type')"
                  :disabled="!checkData.checkCategory"
                  :options="checkTypes"
                  :colors="{
                    text: '#282828',
                    bg: 'white',
                    svg: '#282828',
                    border: '#bcbcbc',
                  }"
                  :config="{ label: 'name', trackBy: 'id' }"
                  :value="checkData.checkType"
                  @input="(value) => updateCheckType(value)"
                  :limit="1"
                  minWidth="102px"
                  maxWidth="100%"
                  :loading="checkTypeFetching"
                />
              </div>
              <FormulateInput
                class="w-full text-base-content"
                :label="$t('pages.email_admin.check_name')"
                :placeholder="$t('pages.email_admin.check_name')"
                type="text"
                validation="required"
                :element-class="
                  (context, classes) => ['flex-1 min-w-full'].concat(classes)
                "
                :disabled="!checkData.checkCategory"
                v-model="checkData.check_name"
              />

              <FormulateInput
                class="w-full text-base-content"
                :label="$t('pages.check_admin.check_label')"
                :placeholder="$t('pages.check_admin.check_label')"
                type="text"
                validation="required"
                :element-class="
                  (context, classes) => ['flex-1 min-w-full'].concat(classes)
                "
                :disabled="!checkData.checkCategory"
                v-model="checkData.label"
              />

              <FormulateInput
                v-model="checkData.entity_types"
                validation="required"
                class="w-full"
                :label="$t('pages.field_table.entity_type')"
                :placeholder="$t('pages.field_table.select_entity_type')"
                type="multi-select"
                variant="alt"
                :element-class="
                  (context, classes) => ['flex-1 min-w-full'].concat(classes)
                "
                :options="entityTypeOptions"
                :loading="isEntityTypesLoading"
                :config="{
                  ...ENTITY_TYPES_DROPDOWN_CONFIG,
                  limit: 4,
                }"
                :disabled="!checkData.checkCategory"
              />

              <FormulateInput
                class="w-full text-base-content"
                :label="$t('pages.check_admin.check_description')"
                :placeholder="$t('pages.check_admin.check_description')"
                type="textarea"
                validation="required"
                :element-class="
                  (context, classes) => ['flex-1 min-w-full'].concat(classes)
                "
                :disabled="!checkData.checkCategory"
                v-model="checkData.description"
              />
              <!-- <FormulateInput
                class="w-full text-base-content"
                label="Sla Period (days)"
                placeholder="Sla Period (days)"
                type="text"
                min="1"
                validation="required|number|min:1,number"
                :element-class="
                  (context, classes) => ['flex-1 min-w-full'].concat(classes)
                "
                :disabled="!checkData.checkCategory"
                v-model="checkData.sla"
              /> -->
            </div>
            <div class="card-actions justify-end">
              <Button
                :loader="createCheckLoading"
                :text="$t('pages.check_admin.create_check')"
                type="primary"
                :disabled="
                  hasErrors ||
                    createCheckLoading ||
                    !checkData.checkCategory ||
                    !checkData.checkType
                "
                @click="handleCreateCheck()"
              />
            </div>
          </div>
        </FormulateForm>
      </template>
      <template #footer>
        <div></div>
      </template>
    </modal-content>
    <!-- End Create Check Modal -->

    <CheckPackagesModal
      @closed="setCheckPackages(null)"
      @handleFilterResult="handleFilterResult($event, 'package')"
      :checkData="checkPackages"
      :packages="getCheckPackages"
      :isLoading="fetchingCheckPackages"
    />
  </div>
</template>
<script>
import SubHeader from "@/components/SubHeader";
import Table from "@shared/dashboard-components/table-large";
import axios from "@/axios";
import { debounce } from "lodash";
import modalContent from "@shared/modal-content";
// import Input from "@/components/input";
import Button from "@/components/button";
// import { uuid } from "vue-uuid";
import Dropdown from "@/components/dropdown-base";
import CheckPackagesModal from "./check-dragable-section/components/checkPackagesModal.vue";
import { checkPermission } from "@shared/utils/functions";
import { EventBus } from "@/main.js";
import { DateTime } from "luxon";
import { FormatDateNew } from "@/plugins/functions";
import { mapActions, mapState } from "vuex";
import { ENTITY_TYPES_DROPDOWN_CONFIG } from "@shared/utils/constants";
import { caseEntityTypesMap } from "@shared/modules/localization/utils/caseEntityTranslation/constants";
import { translateEntity } from "@shared/modules/localization/utils/caseEntityTranslation"
import { getFilterOptions } from "@shared/dashboard-components/table-large/utils";

export default {
  name: "check-admin",
  title: "Checks",

  components: {
    SubHeader,
    Table,
    modalContent,
    //   Input,
    Button,
    Dropdown,
    CheckPackagesModal,
  },
  props: {},
  data: () => ({
    getFilterOptions,
    ENTITY_TYPES_DROPDOWN_CONFIG,
    searchTimeout: null,
    searchLoading: {},
    columnFilterOptionsAsync: null,
    isEntityTypesLoading: false,
    serverParams: null,
    checks: [],
    isLoading: false,
    isCloning: false,
    searchValue: "",
    createCheckLoading: false,
    checkData: {
      check_name: "",
      check_label: "",
      description: "",
      sla: 1,
      checkType: null,
      checkCategory: null,
      entity_types: [],
    },
    checkTypeFetching: false,
    checkTypes: [],
    checkCategories: [],
    checkPackages: null,
    paginationOptions: {
      enabled: true,
      mode: "remote",
      perPage: 10,
    },
    pageData: {
      total: 0,
    },
    currentPageData: {
      page: 1,
      count: 10,
    },
    fetchingCheckPackages: false,
    checksPackages: [],
    searchParams: {
      package: "",
    },
    sortParams: null,
  }),
  async created() {
    EventBus.$on("fetchCheckList", () => {
      this.fetchChecksList();
    });
  },
  computed: {
    ...mapState({
      entityTypeOptions: "entityTypeList",
    }),
    translatedRows() {
      return this.checks.map(this.translateEntity);
    },
    columns() {
      return [
        {
          label_key: 'pages.field_table.actions',
          field: "actions",
          config: { filter: false, type: "actions" },
          sortable: false,
        },
        {
          label_key: 'pages.field_table.lock',
          field: "lock",
          config: { filter: false, type: "lock", tab: "Check in use" },
          tdClass: "truncate max-w-xs",
          sortable: false,
        },
        {
          label_key: 'pages.field_table.in_use',
          field: "in_use",
          config: { filter: false, type: "tooltip_text" },
          tdClass: "truncate max-w-xs",
          width: "125px",
        },
        {
          label_key: 'pages.field_table.name',
          field: "name",
          config: {
            filter: {
              nameModifier: (el) => this.translateEntity(el).name,
            },
            type: "link"
          },
          tdClass: "truncate max-w-sm",
        },
        {
          label_key: 'pages.field_table.label',
          field: "label",
          config: {
            filter: {
              nameModifier: (el) => this.translateEntity(el).label,
            },
            type: "",
          },
          tdClass: "truncate max-w-sm",
        },
        {
          label_key: 'pages.check_admin.category',
          field: "category",
          query_key: "check_type",
          config: {
            filter: {
              nameModifier: (el) => this.translateEntity(el).category,
            },
            type: "select",
          },
          tdClass: "truncate max-w-sm",
        },
        {
          label_key: 'pages.field_table.type',
          field: "subtype_name",
          query_key: "subtype",
          sortable: false,
          config: {
            filter: {
              nameModifier: (el) => this.translateEntity(el).type,
            },
            type: "select",
          },
          tdClass: "truncate max-w-sm",
        },
        {
          label_key: 'pages.field_table.source',
          field: "is_custom",
          query_key: "source",
          sortable: false,
          prefetchedOptions: [
            { name: "Custom", value: "custom" },
            { name: "System Provided", value: "system_provided" },
          ],
          config: { filter: true, type: "source" },
          tdClass: "truncate max-w-sm",
        },
        {
          label_key: 'pages.check_admin.packages',
          field: "in_packages",
          sortable: false,
          config: { filter: false, type: "show_info" },
          tdClass: "truncate max-w-sm",
        },

        {
          label_key: 'pages.field_table.created_by',
          field: "created_by",
          config: { filter: false, type: "tooltip_text" },
          tdClass: "truncate max-w-xs",
          width: "120px",
        },
        {
          label_key: 'pages.field_table.created_at',
          field: "created_at",
          config: { filter: true, type: "tooltip_text" },
          tdClass: "truncate max-w-xs",
          width: "120px",
        },
        {
          label_key: 'pages.block_admin.last_updated_by',
          field: "modified_by",
          config: { filter: false, type: "tooltip_text" },
          tdClass: "truncate max-w-xs",
          width: "185px",
        },
        {
          label_key: 'pages.block_admin.last_updated_on',
          field: "last_updated",
          config: { filter: true, type: "tooltip_text" },
          tdClass: "truncate max-w-xs",
          width: "185px",
        },
        // {"label":"Automatic/Manual","field":"is_automatic"},
      ]
    },
    columnFilterOptions() {
      let cols = this.getFilterOptions(this.columns, this.checks);
      return cols;
    },
    columnOption() {
      return this.columns.map((el) => ({
        ...el,
        label: this.$t(el.label_key),
        config: {
          ...el.config,
          isLoading: !!this.searchLoading[el.field] || false,
        },
      }));
    },
    getCheckPackages() {
      return this.searchParams?.package ? this.checksPackages?.filter((el) =>
        el?.name
          ?.toLowerCase()
          .includes(this.searchParams?.package?.toLowerCase())
      ) : this.checksPackages || [];
    },
  },

  async mounted() {
    await this.fetchChecksList();
  },

  methods: {
    ...mapActions(["fetchEntityTypeList"]),
    FormatDateNew,
    checkPermission,
    translateEntity(entity) {
      return translateEntity(entity, caseEntityTypesMap.CHECK);
    },
    currentPageDataOpt() {
      return { ...this.currentPageData, perPage: this.currentPageData.count };
    },
    closeCheckModal() {
      this.$refs["check-modal"].hideModal();
    },
    updateCheckCategory(value) {
      this.checkData.checkCategory = value;
    },
    updateCheckType(value) {
      this.checkData.checkType = value;
    },
    async showCheckModal() {
      await this.$store.dispatch("fetchPermissions");
      if(!this.checkPermission("check.create")) {
          this.$toast.error(this.$t('pages.check_admin.permission_to_create_the_check'));
          return;
      }
      this.$refs["check-modal"].showModal();
      this.checkData = {
        check_name: "",
        description: "",
        sla: 1,
        checkType: null,
        checkCategory: null,
      };
      await this.fetchCheckTypes();
      await this.getSubCheckTypes();
      await this.fetchEntityTypeOptions();
    },
    // get checks list
    async fetchChecksList() {
      this.isLoading = true;
      const tenant = this.$store.getters.getTenantId;
      let qParams = new URLSearchParams(this.currentPageData);
      let filterParams = {};

      const getFilterValue = (key) => {
        return this.serverParams[key].value;
      };
      if (this.serverParams) {
        Object.keys(this.serverParams).map((key) => {
          // if that key exist
          // add it to filter else we'll delete it
          // so it wont make empty strings in parameters
          filterParams[key] = this.serverParams[key]
            ? getFilterValue(key)
            : delete filterParams[key];
        });
        if (filterParams.created_at) {
          filterParams.created_at = DateTime.fromFormat(
            filterParams.created_at,
            "dd MMM yyyy"
          )
            .plus({ days: 0 })
            .toISODate();
        }
        if (filterParams.last_updated) {
          filterParams.last_updated = DateTime.fromFormat(
            filterParams.last_updated,
            "dd MMM yyyy"
          )
            .plus({ days: 0 })
            .toISODate();
        }
      }
      let url = `/tenant-check/${tenant}?${qParams}`;
      let { data } = await axios.get(url, {
        params: {
          ...filterParams,
          ...this.sortParams,
        },
      });
      try {
        // const formatDate = (date) => (date ? DateTime.fromISO(date).setZone('UTC').toFormat("dd MMM yyyy") : "");
        let checksData = data?.data?.map((el) => {
          if (el.created_at || el.last_updated) {
            el.created_at = FormatDateNew(new Date(el.created_at));
            el.last_updated = FormatDateNew(new Date(el.last_updated));
            return el;
          }
          if (el.parameters && el.parameters !== "null") {
            let data = JSON.parse(el.parameters);
            return { ...el, parameters: data.parameters };
          } else {
            return { ...el };
          }
        });
        this.checks = checksData;
        this.pageData = data?.page;
        this.paginationOptions.perPage = data?.page.per_page;
      } catch (error) {
        console.log(error, "Error");
      }
      // this.columns = columns;
      this.isLoading = false;
    },
    // end checks list

    // fetch all check types

    async fetchCheckTypes() {
      this.checkTypeFetching = true;
      try {
        let url = `/tenant-check/type/checks`;
        let { data } = await axios.get(url);
        this.checkCategories = data.check_types;
      } catch (error) {
        console.log(error, "Error");
      }
      this.checkTypeFetching = false;
    },

    // end fetch check types

    async fetchEntityTypeOptions() {
      this.isEntityTypesLoading = true;
      if (!this.entityTypeOptions.length) {
        await this.fetchEntityTypeList();
      }
      this.isEntityTypesLoading = false;
    },

    handleRowClick(data) {
      this.$router.push({
        name: "check admin details",
        params: { id: data.id },
      });
    },

    // Create Check

    async handleCreateCheck() {
      this.checkData.check_name = this.checkData.check_name.trim();
      this.checkData.description = this.checkData.description.trim();
      this.checkData.label = this.checkData.label.trim();
      if (
        this.checkData.check_name.length === 0 ||
        this.checkData.description.length === 0 || 
        this.checkData.label.length === 0
      ) {
        this.$toast.error(this.$t('pages.block_admin.field_should_not_be_filled_with_spaces'));
        return;
      } else {
        this.createCheckLoading = true;
        let url = `/tenant-check/custom-check`;
        if (this.checkData.sla > 0) {
          let payload = {
            tenant_id: this.$store.getters.getTenantId,
            check_type_id: this.checkData.checkCategory.id,
            check_name: this.checkData.check_name,
            description: this.checkData.description,
            sla: this.checkData.sla,
            check_sub_type_id: this.checkData.checkType.id,
            entity_types: this.checkData.entity_types.map((el) => ({
              entity_type_id: el.id,
            })),
            label: this.checkData?.label,
          };
          try {
            let { data } = await axios.post(url, payload);
            let checkData = {
              description: payload.description,
              id: data.check_id.id,
              is_custom: true,
              name: payload.check_name,
              parameters: null,
              sla: payload.sla,
              tenant_id: this.$store.getters.getTenantId,
            };
            this.checks.push(checkData);
            console.log(checkData);
            this.$router.push({
              name: "check admin details",
              params: { id: checkData.id, action: "edit" },
            });
            this.$toast.success(data.message);
            this.closeCheckModal();
          } catch (error) {
            this.createCheckLoading = false;
            this.$toast.error(
              error.response.data.detail || this.$t('pages.check_admin.failed_to_create_new_check')
            );
          }
        } else {
          this.createCheckLoading = false;
          return this.$toast.warning(this.$t('pages.check_admin.sla_period_should_be_greated_than_0'));
        }
        this.createCheckLoading = false;
      }
    },

    // End Create Check

    // clone check

    async handleCloneCheck(check) {
      if (this.checkPermission("check.create")) {
        // this.isCloning = true
        let url = `/tenant-check/${check.id}/clone`;
        let payload = {
          tenant_id: this.$store.getters.getTenantId,
          check_name: check.name,
        };
        try {
          let { data } = await axios.post(url, payload);
          this.$router.push({
            name: "check admin details",
            params: { id: data.check_id, action: "edit" },
          });
          this.$toast.success(data.message);
        } catch (error) {
          this.$toast.error(
            error.response.data.detail || this.$t('pages.check_admin.unable_to_clone_check')
          );
        }
        // } else {
        //     this.$toast.error("Access Denied! You are not authorized to perform this operation.");
      }
    },
    handleSearchFilter: debounce(function(value) {
      this.searchValue = value;
    }, 1000),

    // end Clone Check

    // addCustomCheckType (newTag) {
    //     let custom_type ={
    //         id: uuid.v4(),
    //         name: newTag,
    //         custom: true
    //     }
    //     this.checkTypes.push(custom_type),
    //     this.checkData.checkType = custom_type
    // },

    // Get subcategory lists
    async getSubCheckTypes() {
      this.checkTypeFetching = true;
      try {
        let url = `/tenant-check/type/checks/sub-types`;
        let { data } = await axios.get(url);
        this.checkTypes = data;
      } catch (error) {
        console.log(error, "Error");
      }
      this.checkTypeFetching = false;
    },
    async setCheckPackages(payload) {
      if (!checkPermission("check.edit")) {
        return;
      }
      this.checksPackages = [];
      if (payload && payload.in_packages > 0) {
        this.$modal.show("checkPackagesModal");
        this.checkPackages = payload;
        this.fetchingCheckPackages = true;
        const { data: result } = await axios.get(
          `tenant-check/${payload.tenant_id}/${payload.id}/packages`
        );
        this.checksPackages = result.packages;
        this.fetchingCheckPackages = false;
      } else {
        this.handleFilterResult(null, 'package')
      }
    },
    isRestrictedOrCustom(check) {
      if (check.is_custom) {
        return "Custom";
      } else if (check.is_restricted) {
        return "Restricted";
      } else {
        return "System Provided";
      }
    },
    async onPageChange(data, resetPage = false) {
      this.currentPageData = {
        page: data.currentPage,
        count: data.currentPerPage,
      };
      if (resetPage) this.currentPageData.page = 1;
      if (!resetPage) {
        await this.$nextTick();
        const scrollTarget = document.getElementById("checkTable");
        if (scrollTarget) {
          scrollTarget.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }
      await this.fetchChecksList();
    },

    getSearchOptionsTick(payload) {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      const delay = 500;
      this.searchTimeout = setTimeout(() => {
        this.getSearchOptions(payload);
      }, delay);
    },

    async getSearchOptions(payload) {
      console.log("payload :>> ", payload);
      if (!payload.value) this.columnFilterOptionsAsync = null;
      else {
        this.$set(this.searchLoading, payload.column.field, true);
        try {
          const requestData = {
            ...this.serverParams,
            [payload.column.query_key || payload.column.field]: payload.value,
          };
          const { data } = await axios.get(
            `/tenant-check/${this.$store.getters.getTenantId}`,
            {
              params: requestData,
            }
          );
          this.columnFilterOptionsAsync = this.getFilterOptions(
            this.columns,
            data.data
          );
        } catch (error) {
          console.log("getSearchOptions :>> ", error);
        }

        this.$set(this.searchLoading, payload.column.field, false);
      }
    },

    async onColumnFilters({ columnFilters }) {
      this.currentPageData.page = 1;
      this.paginationOptions.perPage = this.currentPageData.count;
      this.serverParams = columnFilters ? { ...columnFilters } : null;
      await this.fetchChecksList();
    },

    async clearFilters() {
      this.serverParams = null;
      await this.fetchChecksList();
    },
    handleFilterResult(data) {
      this.searchParams.package = data;
    },
    async onSortChange(data) {
      if (data && data.length && data[0].type !== "none") {
        data = data[0];
        this.sortParams = {
          sort: data.field + ":" + data.type,
        };
        await this.fetchChecksList();
      } else {
        this.sortParams = null;
        await this.fetchChecksList();
      }
    },
  },
};
</script>

<style lang="scss">
.tooltip-text {
  display: none;
  position: absolute;
  z-index: 999 !important;
  width: auto;
  color: white;
  font-size: 12px;
  background-color: #192733;
  border-radius: 10px;
  padding: 10px 15px 10px 15px;
}

.hover-text:hover .tooltip-text {
  display: block;
}

#right {
  left: 70px;
  top: -30px;
}

// .modalContent .modalContent__footer{
//     display:none !important;
// }

// #bottom {
//   top: 25px;
//   left: -50%;
// }

// #bottom::before {
//   top: -5%;
//   left: 45%;
// }

// #left {
//   top: -8px;
//   right: 120%;
// }

// #left::before {
//   top: 35%;
//   left: 94%;
// }

// #right {
//   top: -8px;
//   left: 120%;
// }

// #right::before {
//   top: 35%;
//   left: -2%;
// }

.hover-text {
  position: relative;
}
.add-check__modal-content {
  display: grid;
  grid-template-rows: 1fr auto
}
</style>
